import React, { useState, useEffect, useContext, useRef } from "react"

// eslint-disable-next-line
import { Alert, Badge, Button, Card, CardHeader, CardBody, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, Label, Input, Form, Table } from 'reactstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SessionContext from "SessionContext";
import ProgressLoader from 'partials/progress-loader';
import SingleFieldMapper, { TruncatedText } from './single-field-mapper';
import { OPERATION_LABELS } from './sync-behavior-selector';

export default function FieldMapper({
  sourceId,
  orderId,
  orderName,
  destinationName,
  destinationId,
  destinationObject,
  destinationObjectFields,
  allowCustomFields = false, // true for Google Sheets, false for databases like Postgres
  supportsFieldOrdering = false,
  selectedOperation,
  onComplete,
  onFieldsRefresh,
  onMappingChange,
  onCancel,
  hasMappingChanges,
  fieldsRefreshTrigger,
}) {
  const session = useContext(SessionContext);
  const [loading, setLoading] = useState(true);
  const [isRefreshingFields, setIsRefreshingFields] = useState(false);
  const [sourceObjectColumns, setSourceObjectColumns] = useState([]);
  const [columns, setColumns] = useState([]);
  const [upsertKeyColumns, setUpsertKeyColumns] = useState([]);
  const [primaryMapping, setPrimaryMapping] = useState({ from: "", to: "" })
  const [additionalMappings, setAdditionalMappings] = useState([])
  const [primaryMappingWarning, setPrimaryMappingWarning] = useState(null)
  const [primaryMappingRequired, setPrimaryMappingRequired] = useState(false);
  const [sortOrder, setSortOrder] = useState('alphabetical'); // 'alphabetical' or 'mapping'
  const [confirmRefreshModal, setConfirmRefreshModal] = useState(false);
  const [confirmSyncModal, setConfirmSyncModal] = useState(false);
  const [confirmRemoveIncompleteModal, setConfirmRemoveIncompleteModal] = useState(false);

  // Refresh destination object fields
  const handleRefreshFields = async () => {
    if (!destinationId || !destinationObject?.full_name) return;
    setIsRefreshingFields(true);
    try {
      const response = await session.command('census/refresh-destination-object-fields', {
        census_destination_id: destinationId,
        full_name: destinationObject.full_name,
      });
      await pollForFieldsRefresh(response.refresh_key);
    } catch (err) {
      console.error("Error refreshing destination fields:", err);
    } finally {
      setIsRefreshingFields(false);
    }
  };

  const pollForFieldsRefresh = async (refreshKey, interval = 1000, timeout = 30000) => {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      const response = await session.query('census/refresh-destination-fields-status', {
        census_destination_id: destinationId,
        full_name: destinationObject.full_name,
        refresh_key: refreshKey,
      });
      // Handle both string response ('completed') and object response ({ status: 'completed' })
      const isCompleted = response === 'completed' || response?.status === 'completed';
      if (isCompleted) {
        // Fetch the updated fields after refresh completes
        await fetchDestinationObjectFields();
        return response;
      }
      await new Promise(r => setTimeout(r, interval));
    }
    throw new Error("Fields refresh status polling timed out");
  };

  // Fetch destination object fields and update parent via callback
  const fetchDestinationObjectFields = async () => {
    try {
      const response = await session.query('census/destination-object', {
        census_destination_id: destinationId,
        full_name: destinationObject.full_name,
      });
      if (onFieldsRefresh && response?.fields) {
        onFieldsRefresh(response.fields);
      }
    } catch (err) {
      console.error("Error fetching destination object fields:", err);
    }
  };

  // Trigger field refresh when destination objects are refreshed from parent
  useEffect(() => {
    if (fieldsRefreshTrigger > 0) {
      handleRefreshFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsRefreshTrigger]);

  // Get sorted mappings with original indices preserved
  const getSortedMappings = (mappings) => {
    const mappingsWithIndex = mappings.map((mapping, index) => ({ mapping, originalIndex: index }));
    if (sortOrder === 'alphabetical') {
      return mappingsWithIndex.sort((a, b) => {
        const aTo = (a.mapping.to || '').toLowerCase();
        const bTo = (b.mapping.to || '').toLowerCase();
        return aTo.localeCompare(bTo);
      });
    }
    return mappingsWithIndex; // 'mapping' order - preserve original order
  };

  // Handle drag end for reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceDisplayIndex = result.source.index;
    const destDisplayIndex = result.destination.index;

    if (sourceDisplayIndex === destDisplayIndex) return;

    // Translate display indices back to original indices in additionalMappings
    const visibleMappings = getSortedMappings(additionalMappings)
      .filter(({ mapping }) => mapping.from && mapping.to);

    const sourceOriginalIndex = visibleMappings[sourceDisplayIndex].originalIndex;
    const destOriginalIndex = visibleMappings[destDisplayIndex].originalIndex;

    const newMappings = [...additionalMappings];
    const [removed] = newMappings.splice(sourceOriginalIndex, 1);
    // Adjust target if source was before destination (splice shifted indices)
    const targetIndex = sourceOriginalIndex < destOriginalIndex ? destOriginalIndex - 1 : destOriginalIndex;
    newMappings.splice(targetIndex, 0, removed);
    setAdditionalMappings(newMappings);
    onMappingChange?.();
  };

  // Helper function to get default sync key mapping based on decision tree
  const getDefaultSyncKeyMapping = (destFields, sourceColumns) => {
    // Source: always use am_id as the primary identifier (our standard record key)
    const fromValue = 'am_id';

    // Destination: follow decision tree
    // Use destinationObjectFields for checking unique keys (not destFields which is mixed with source)
    const actualDestFields = destinationObjectFields || [];

    // Step 1: Try to find am_id in destination WITH unique constraint
    const amIdDestField = actualDestFields.find(f =>
      (f.full_name?.toLowerCase() === 'am_id' ||
       f.name?.toLowerCase() === 'am_id' ||
       f.label?.toLowerCase() === 'am_id' ||
       f.full_name?.split('.').pop()?.toLowerCase() === 'am_id') &&
      (f.can_be_upsert_key || f.can_be_update_key)
    );

    if (amIdDestField) {
      return {
        from: fromValue,
        to: amIdDestField.full_name || amIdDestField.name,
        safe: true,
        warning: null,
      };
    }

    // Step 2: Check for any existing unique fields first (prefer existing over creating new)
    const safeFields = actualDestFields.filter(f =>
      f.can_be_upsert_key || f.can_be_update_key
    );

    // Step 3: If we have existing unique fields, use one of them
    if (safeFields.length > 0) {
      // Choose best available safe field (prefer 'id', then 'am_id')
      const defaultField = safeFields.find(f =>
        f.label?.toLowerCase() === 'id' ||
        f.full_name?.toLowerCase() === 'id' ||
        f.full_name?.split('.').pop()?.toLowerCase() === 'id'
      ) || safeFields.find(f =>
        f.label?.toLowerCase() === 'am_id' ||
        f.full_name?.toLowerCase() === 'am_id' ||
        f.full_name?.split('.').pop()?.toLowerCase() === 'am_id'
      ) || safeFields[0];

      const fieldName = defaultField.label || defaultField.full_name?.split('.').pop() || defaultField.full_name;
      const isAmId = fieldName?.toLowerCase() === 'am_id';

      return {
        from: fromValue,
        to: defaultField.full_name || defaultField.name,
        safe: true,
        warning: isAmId ? null : `Using "${fieldName}" as the primary identifier. Ensure this field uniquely identifies each record.`,
      };
    }

    // Step 4: No unique fields - error (primary identifier requires an existing unique field)
    return {
      from: fromValue,
      to: "",
      safe: false,
      error: 'No unique identifier field found in the destination table. A primary identifier is required for this sync operation but cannot be auto-created for this destination type.',
      suggestion: 'Add a column with a PRIMARY KEY or UNIQUE constraint to your destination table (e.g., "am_id") before creating this sync.',
      blockSync: true,
    };
  };

  // Use actual destination fields when available, falling back to source columns
  // only when no destination fields are defined (e.g., new database tables)
  const effectiveDestinationFields = (() => {
    // Normalize source columns
    const normalizedSourceCols = sourceObjectColumns.map(col => ({
      ...col,
      full_name: col.full_name || col.name,
      label: col.label || col.name,
    }));

    // If no destination fields:
    // - For custom-field destinations (Google Sheets), return empty so custom fields are created
    // - For databases, use source columns as fallback
    if (!destinationObjectFields?.length) {
      return allowCustomFields ? [] : normalizedSourceCols;
    }

    // Use actual destination fields - these represent the real fields available in the destination
    const normalizedDestFields = destinationObjectFields.map(field => ({
      ...field,
      full_name: field.full_name || field.name,
      label: field.label || field.name,
    }));

    return normalizedDestFields;
  })();

  // Check for primary identifier mapping usage on the selected destination object
  // --- This is not needed for mirror/append operations where no primary identifier is required
  const checkIfPrimaryMapping = () => {
    const _primaryIdRequired = destinationObject?.primary_identifier_mapping_attributes[selectedOperation]?.required;
    setPrimaryMappingRequired(_primaryIdRequired);
    return _primaryIdRequired;
  }

  // Reset mappings when destination object changes
  useEffect(() => {
    setAdditionalMappings([]);
    setPrimaryMapping({ from: "", to: "" });
  }, [destinationObject?.full_name]);

  // Initialize primary mapping and additional mappings when data is ready
  useEffect(() => {
    if (!sourceObjectColumns?.length) return;

    // Build destFields: all source columns + any additional destination fields
    const normalizedSourceCols = sourceObjectColumns.map(col => ({
      ...col,
      full_name: col.full_name || col.name,
      label: col.label || col.name,
    }));

    // Start with source columns as fallback (for destinations with no fields defined yet).
    // When actual destination fields are available, use those instead.
    let destFields = allowCustomFields ? [] : normalizedSourceCols;
    if (destinationObjectFields?.length) {
      destFields = destinationObjectFields.map(field => ({
        ...field,
        full_name: field.full_name || field.name,
        label: field.label || field.name,
      }));
    }

    // Initialize primary mapping if not set, or re-evaluate if destination fields changed
    // Requires source columns to be loaded; destination fields are optional (for Google Sheets)
    let primaryIdentifierTo = primaryMapping.to;
    let primaryIdentifierFrom = primaryMapping.from;

    // Check if we need to re-evaluate the primary mapping:
    // 1. Mapping is not set yet (initial setup)
    // 2. For non-custom-field destinations (Postgres), when destination fields are available and:
    //    - current 'to' field is empty, OR
    //    - current 'to' field doesn't exist in destination
    const currentToField = primaryMapping.to?.toLowerCase();
    const isInitialSetup = !primaryMapping.from && !primaryMapping.to;

    // Check if the current 'to' field exists in destination (only relevant if we have dest fields)
    const toFieldExistsInDest = currentToField && (destinationObjectFields || []).some(f => {
      const fieldName = f.full_name?.toLowerCase() || f.name?.toLowerCase();
      const shortName = f.full_name?.split('.').pop()?.toLowerCase();
      return fieldName === currentToField || shortName === currentToField;
    });

    // For Postgres (non-custom-field), re-evaluate when dest fields are available and current mapping is invalid
    const needsReEvaluationForDb = (
      !allowCustomFields &&
      destinationObjectFields?.length > 0 &&
      (!currentToField || !toFieldExistsInDest)
    );

    const needsReEvaluation = isInitialSetup || needsReEvaluationForDb;

    // Track if we're changing the primary mapping (for resetting additional mappings)
    let primaryMappingChanged = false;

    if (needsReEvaluation && sourceObjectColumns?.length) {
      const defaultMapping = getDefaultSyncKeyMapping(destFields, sourceObjectColumns);

      // Check if the primary mapping is actually changing
      if (defaultMapping.to !== primaryMapping.to || defaultMapping.from !== primaryMapping.from) {
        primaryMappingChanged = !isInitialSetup; // Only flag as changed if not initial setup
      }

      primaryIdentifierTo = defaultMapping.to;
      primaryIdentifierFrom = defaultMapping.from;

      setPrimaryMapping({
        from: defaultMapping.from,
        to: defaultMapping.to,
      });

      // Set warning/error state if needed
      if (defaultMapping.warning) {
        setPrimaryMappingWarning({ type: 'warning', message: defaultMapping.warning });
      } else if (defaultMapping.error) {
        setPrimaryMappingWarning({
          type: 'error',
          message: defaultMapping.error,
          suggestion: defaultMapping.suggestion,
          blockSync: defaultMapping.blockSync || false,
        });
      } else {
        setPrimaryMappingWarning(null);
      }
    }

    // Initialize additional mappings if empty OR if primary mapping changed (need to re-exclude new primary field)
    const shouldInitAdditionalMappings = additionalMappings.length === 0 || primaryMappingChanged;

    if (shouldInitAdditionalMappings) {
      // Check if primary mapping is required for this operation
      const isPrimaryRequired = destinationObject?.primary_identifier_mapping_attributes?.[selectedOperation]?.required;

      // Exclude primary identifier fields from additional mappings
      const primaryDestFieldName = primaryIdentifierTo?.toLowerCase() || '';
      const primarySourceFieldName = primaryIdentifierFrom?.toLowerCase() || '';

      // Only exclude primary identifier fields if primary mapping is required
      // Otherwise include all fields (including am_id) in additional mappings
      const excludedSourceNames = new Set(
        isPrimaryRequired
          ? ['am_id', primarySourceFieldName].filter(Boolean)
          : []
      );
      const excludedDestNames = new Set(
        isPrimaryRequired
          ? ['am_id', primaryDestFieldName].filter(Boolean)
          : []
      );

      const columnsForAdditional = sourceObjectColumns.filter(col =>
        !excludedSourceNames.has(col.name?.toLowerCase())
      );
      const destFieldsForAdditional = destFields.filter(f => {
        const fieldName = f.full_name?.toLowerCase() || f.name?.toLowerCase() || '';
        const shortName = f.full_name?.split('.').pop()?.toLowerCase() || '';
        return !excludedDestNames.has(fieldName) && !excludedDestNames.has(shortName);
      });

      // Check for required destination fields first (excluding primary identifier)
      const requiredFields = destinationObjectFields?.filter((field) => {
        const fieldName = field.full_name?.toLowerCase() || '';
        const shortName = field.full_name?.split('.').pop()?.toLowerCase() || '';
        return field.required_for_mapping === true &&
               !excludedDestNames.has(fieldName) &&
               !excludedDestNames.has(shortName);
      }) || [];

      if (requiredFields.length > 0) {
        // Create mappings for required fields, matching source columns by name or index
        const requiredMappings = requiredFields.map((field, idx) => {
          const fieldName = field.full_name?.split('.').pop()?.toLowerCase() || field.label?.toLowerCase();
          const matchingCol = columnsForAdditional.find(
            col => col.name.toLowerCase() === fieldName
          );
          return {
            from: matchingCol?.name || columnsForAdditional[idx]?.name || "",
            to: field.full_name,
            isRequired: true,
          };
        });
        setAdditionalMappings(requiredMappings);
      } else {
        // No required fields - create mappings for each source column (excluding primary identifier)
        // Track used destination fields to prevent duplicates
        const usedDestFields = new Set();

        const mappings = columnsForAdditional.map((col, idx) => {
          const matchingField = destFieldsForAdditional.find(
            f => f.label?.toLowerCase() === col.name.toLowerCase() ||
                 f.full_name?.toLowerCase() === col.name.toLowerCase() ||
                 f.full_name?.split('.').pop()?.toLowerCase() === col.name.toLowerCase()
          );

          // Only use matching field if it hasn't been used yet
          const destField = matchingField && !usedDestFields.has(matchingField.full_name)
            ? matchingField
            : null;

          // Check if the destination field is required (NOT NULL without DEFAULT in Postgres)
          const fieldIsRequired = destField?.required_for_mapping === true;

          // Track used destination field
          if (destField) {
            usedDestFields.add(destField.full_name);
          }

          // If no matching field and custom fields are allowed, auto-create with source column name
          if (!destField && allowCustomFields) {
            return {
              from: col.name,
              to: col.name,
              isRequired: false,
              isCustom: true,
              isAutoCreated: true, // Distinguishes auto-created from user-created custom fields
            };
          }

          return {
            from: col.name,
            to: destField?.full_name || destField?.name || "",
            isRequired: fieldIsRequired,
            ...(destField ? { isAutoMapped: true } : {}),
          };
        });

        // Add any destination fields that don't have corresponding source columns
        const sourceColNames = new Set(columnsForAdditional.map(col => col.name.toLowerCase()));
        const usedDestFieldNames = new Set(mappings.map(m => m.to?.toLowerCase()).filter(Boolean));
        const unmatchedDestFields = destFieldsForAdditional.filter(field => {
          const fieldName = field.full_name?.split('.').pop()?.toLowerCase() || field.label?.toLowerCase();
          return !sourceColNames.has(fieldName) && !usedDestFieldNames.has(field.full_name?.toLowerCase());
        });

        const unmatchedMappings = unmatchedDestFields.map(field => ({
          from: "",
          to: field.full_name,
          isRequired: field.required_for_mapping === true,
        }));

        setAdditionalMappings([...mappings, ...unmatchedMappings]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceObjectColumns, destinationObjectFields, allowCustomFields, selectedOperation, destinationObject]);

  useEffect(() => {
    if(!destinationId || !orderId || !sourceId) { return; }
    session.query('census/source-table-columns', { census_source_id: sourceId, order_id: orderId })
      .then((response) => {
        setSourceObjectColumns(response.columns || []);
        setColumns(response.columns || []);
        session.query('census/get-destination-objects', { census_destination_id: destinationId })
          .then((response) => {
            setLoading(false);
          })
          .catch((err) => {
            console.log("Error fetching destination objects:", err);
          });
      })
      .catch((err) => {
        console.log("Error fetching source table columns:", err);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[destinationId, orderId, sourceId]);

  useEffect(() => {
    if(!Object.keys(sourceObjectColumns).length) { return; }
    // Get columns that can be primary identifiers based on operation
    const _upsertKeyColumns = sourceObjectColumns?.filter((col) => {
      return col.can_be_upsert_key || [];
    });
    setUpsertKeyColumns(_upsertKeyColumns);
  },[sourceObjectColumns]);

  // Check if primary identifier is required for the selected operation
  // and reset mappings when this changes so they get re-initialized correctly
  const prevPrimaryRequiredRef = useRef(primaryMappingRequired);
  useEffect(() => {
    const wasRequired = prevPrimaryRequiredRef.current;
    checkIfPrimaryMapping();
    const isRequired = destinationObject?.primary_identifier_mapping_attributes?.[selectedOperation]?.required;

    // If primary requirement changed, reset mappings to trigger re-initialization
    if (wasRequired !== isRequired && additionalMappings.length > 0) {
      setAdditionalMappings([]);
      setPrimaryMapping({ from: "", to: "" });
    }
    prevPrimaryRequiredRef.current = isRequired;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperation, destinationObject]);

  // Get columns that can be primary identifiers based on operation
  // const upsertKeyColumns = columns?.filter((col) => col.can_be_upsert_key) || []

  // Get destination fields that can be primary identifiers based on operation
  // Only returns fields with unique constraints - no fallback to non-unique fields
  const getPrimaryIdentifierFields = () => {
    if (!destinationObjectFields?.length) return [];

    // Filter to only fields with unique constraints based on operation
    const uniqueFields = destinationObjectFields.filter((field) => {
      switch (selectedOperation) {
        case "update":
        case "delete":
          return field.can_be_update_key
        case "insert":
          return field.can_be_insert_key
        default:
          // upsert, mirror, append
          return field.can_be_upsert_key
      }
    });

    // Only return fields with unique constraints - no fallback
    return uniqueFields;
  }

  const upsertKeyFields = getPrimaryIdentifierFields()

  // Get available fields for primary identifier (excluding fields used in additional mappings)
  const getAvailableFieldsForPrimaryIdentifier = () => {
    const usedFields = additionalMappings.map((m) => m.to).filter(Boolean)
    return upsertKeyFields.filter((field) => {
      return !usedFields.includes(field.full_name);
    });
  }

  // Get available fields for additional mappings (excluding used ones and primary identifier)
  const getAvailableFieldsForMapping = (currentMappingIndex) => {
    const usedFields = additionalMappings
      .map((m, idx) => (idx !== currentMappingIndex ? m.to : null))
      .filter(Boolean)

    // Also exclude the primary identifier field
    if (primaryMapping.to) {
      usedFields.push(primaryMapping.to)
    }

    return effectiveDestinationFields?.filter((field) => !usedFields.includes(field.full_name)) || []
  }

  const addMapping = () => {
    setAdditionalMappings([...additionalMappings, { from: "", to: "" }])
    onMappingChange?.()
  }

  const removeMapping = (index) => {
    const mapping = additionalMappings[index]

    // Prevent removal of required field mappings
    if (mapping?.isRequired === true) {
      return
    }

    setAdditionalMappings(additionalMappings.filter((_, i) => i !== index))
    onMappingChange?.()
  }

  const removeAllIncompleteMappings = () => {
    setAdditionalMappings(additionalMappings.filter((m) =>
      (m.from && m.to) || m.isRequired
    ))
    onMappingChange?.()
    setConfirmRemoveIncompleteModal(false)
  }

  const updateAdditionalMapping = (index, field, value) => {
    setAdditionalMappings(prev => {
      const newMappings = [...prev]
      newMappings[index] = { ...newMappings[index], [field]: value }
      return newMappings
    })
    onMappingChange?.()
  }

  // Build a map of destination field -> mapping index (for conflict detection)
  const getUsedDestinationFieldsMap = () => {
    const map = {};
    // Add primary mapping
    if (primaryMapping.to) {
      map[primaryMapping.to] = -1; // -1 indicates primary mapping
    }
    // Add additional mappings
    additionalMappings.forEach((m, idx) => {
      if (m.to) {
        map[m.to] = idx;
      }
    });
    return map;
  }

  // Clear the destination field for a given mapping index
  const clearMappingDestination = (index) => {
    if (index === -1) {
      // Clear primary mapping destination
      setPrimaryMapping(prev => ({ ...prev, to: "", isCustom: false }));
    } else {
      // Clear additional mapping destination
      setAdditionalMappings(prev => {
        const newMappings = [...prev];
        newMappings[index] = { ...newMappings[index], to: "", isCustom: false };
        return newMappings;
      });
    }
  }

  // Check if a destination field already exists in the destination object
  const destFieldExists = (destFieldName) => {
    if (!destFieldName) return false;
    const lowerName = destFieldName.toLowerCase();
    const shortName = destFieldName.split('.').pop()?.toLowerCase();
    const destFieldNames = new Set((destinationObjectFields || []).map(f => f.full_name?.toLowerCase()));
    const destFieldLabels = new Set((destinationObjectFields || []).map(f => f.label?.toLowerCase()));
    return destFieldNames.has(lowerName) ||
           destFieldNames.has(shortName) ||
           destFieldLabels.has(lowerName) ||
           destFieldLabels.has(shortName);
  };

  // Build the censusMappings array for the API
  const buildCensusMappings = () => {
    const filteredMappings = additionalMappings.filter((m) => m.from && m.to);
    const sortedMappings = sortOrder === 'alphabetical'
      ? [...filteredMappings].sort((a, b) => (a.to || '').toLowerCase().localeCompare((b.to || '').toLowerCase()))
      : filteredMappings;

    return [
      ...(primaryMappingRequired ? [{
        from: { data: primaryMapping.from, type: "column" },
        to: primaryMapping.to,
        is_primary_identifier: true,
        ...(allowCustomFields && (primaryMapping.isCustom || !destFieldExists(primaryMapping.to)) ? { generate_field: true } : {}),
      }] : []),
      ...sortedMappings.map((mapping) => ({
        from: { data: mapping.from, type: "column" },
        to: mapping.to,
        ...(allowCustomFields && (mapping.isCustom || !destFieldExists(mapping.to)) ? { generate_field: true } : {}),
      })),
    ];
  };

  // "Create Sync" button handler - opens confirmation modal
  const handleCreateSyncClick = () => {
    setConfirmSyncModal(true);
  };

  // "Sync Order Data" button in modal - confirms and triggers API call
  const handleConfirmSync = () => {
    setConfirmSyncModal(false);
    onComplete(buildCensusMappings());
  };

  const isValid = () => {
    // Block sync creation if there's a blocking error (e.g., no unique identifier field)
    // Only applies when a primary mapping is required for this operation
    if (primaryMappingRequired && primaryMappingWarning?.blockSync) {
      return false
    }

    // Must have primary mapping filled out (only if required for this operation)
    if (primaryMappingRequired && (!primaryMapping.from || !primaryMapping.to)) {
      return false
    }

    // All required field mappings must have source column selected
    const hasIncompleteRequiredMappings = additionalMappings.some((m) => m.isRequired === true && !m.from)
    if (hasIncompleteRequiredMappings) {
      return false
    }

    // All optional mappings must be complete or empty
    const optionalMappings = additionalMappings.filter((m) => !m.isRequired)
    return optionalMappings.every((m) => (m.from && m.to) || (!m.from && !m.to))
  }

  if (loading) {
    return (
      <Card className="field-mapper-loading">
        <CardHeader className="field-mapper-header">
          <div className="sync-refresh">
            <h3><strong>3.</strong><span>Map Fields</span></h3>
          </div>
        </CardHeader>
        <CardBody>
          <ProgressLoader />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="field-mapper-header">
        <div className="sync-refresh">
          <h3><strong>3.</strong><span>Map Fields</span></h3>
          {!isRefreshingFields && (
          <Button color="link" size="sm" onClick={() => {
            if (hasMappingChanges) {
              setConfirmRefreshModal(true);
              return;
            }
            handleRefreshFields();
          }} disabled={isRefreshingFields} className="btn-link-census">
            <i className="bi bi-arrow-clockwise" /> refresh fields
          </Button>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {isRefreshingFields && <ProgressLoader />}
        <div>
          <p>
            Map columns from your source table to fields in your destination object. {primaryMappingRequired && 'The primary identifier is required and is used to match records between source and destination.'}
          </p>
        </div>


        {/* Primary Identifier Mapping */}
        {!isRefreshingFields && primaryMappingRequired && (
          <div className="primary-identifier-mapping-container mapping-block" >
            <h3>
              Primary Identifier <span>*</span>
            </h3>
            {primaryMappingWarning && (
              <Alert color={primaryMappingWarning.type === 'error' ? 'danger' : 'warning'} className="alert-icon-left">
                <i className={`bi ${primaryMappingWarning.type === 'error' ? 'bi-x-circle-fill' : 'bi-exclamation-triangle-fill'}`} />
                <div>
                  {primaryMappingWarning.message}
                  {primaryMappingWarning.suggestion && (
                    <div className="mt-2"><strong>Suggestion:</strong> {primaryMappingWarning.suggestion}</div>
                  )}
                </div>
              </Alert>
            )}
            <SingleFieldMapper
              mapping={{ ...primaryMapping, isRequired: true }}
              index={-1}
              columns={upsertKeyColumns}
              allDestinationFields={upsertKeyFields}
              usedDestinationFieldsMap={getUsedDestinationFieldsMap()}
              getAvailableFieldsForMapping={() => getAvailableFieldsForPrimaryIdentifier()}
              updateAdditionalMapping={(index, field, value) => {
                setPrimaryMapping({ ...primaryMapping, [field]: value })
                onMappingChange?.()
              }}
              clearMappingDestination={clearMappingDestination}
              removeMapping={() => {}} // Primary identifier cannot be removed
              allowCustomFields={false} // Primary identifier must be an existing unique field
              disableSource={true} // Source primary identifier is always am_id
            />
          </div>
        )}

        {/* Additional Mappings */}
        {!isRefreshingFields && (
          <div className="additional-mappings-container" >
            <div className="sync-refresh">
              <h3 >
                {primaryMappingRequired && 'Additional '}Field Mappings <small>(Complete)</small>
                {additionalMappings.some((m) => m.isRequired) && (
                  <span >
                    * (Some fields are required by the destination)
                  </span>
                )}
              </h3>
              {additionalMappings.some((m) => !m.from || !m.to) && (
                <Button color="link" size="sm" className="btn-link-census" onClick={() => document.getElementById('incomplete-mappings')?.scrollIntoView({ behavior: 'smooth' })}>
                  <i className="bi bi-arrow-down-circle me-1" />view incomplete mappings
                </Button>
              )}
            </div>
            {supportsFieldOrdering && (
            <div className="sort-options mb-3">
              <Form className="bs-form">
                <Label className="me-2">Sort By:</Label>
                <FormGroup check inline>
                  <Input
                    type="radio"
                    name="sortOrder"
                    id="sortAlphabetical"
                    checked={sortOrder === 'alphabetical'}
                    onChange={() => setSortOrder('alphabetical')}
                  />
                  <Label check for="sortAlphabetical">Alphabetical</Label>
                </FormGroup>
                <FormGroup check inline>
                  <Input
                    type="radio"
                    name="sortOrder"
                    id="sortMapping"
                    checked={sortOrder === 'mapping'}
                    onChange={() => setSortOrder('mapping')}
                  />
                  <Label check for="sortMapping">Mapping order (drag and drop to change the order)</Label>
                </FormGroup>
              </Form>
            </div>
            )}
            {/* Complete mappings (have both source and destination selected) */}
            <div className="complete-mappings mapping-block">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="complete-mappings" isDropDisabled={sortOrder !== 'mapping'}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {getSortedMappings(additionalMappings)
                        .filter(({ mapping }) => mapping.from && mapping.to)
                        .map(({ mapping, originalIndex }, displayIndex) => (
                          <Draggable
                            key={originalIndex}
                            draggableId={`mapping-${originalIndex}`}
                            index={displayIndex}
                            isDragDisabled={sortOrder !== 'mapping'}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <SingleFieldMapper
                                  mapping={mapping}
                                  index={originalIndex}
                                  columns={columns}
                                  allDestinationFields={effectiveDestinationFields}
                                  usedDestinationFieldsMap={getUsedDestinationFieldsMap()}
                                  getAvailableFieldsForMapping={getAvailableFieldsForMapping}
                                  updateAdditionalMapping={updateAdditionalMapping}
                                  clearMappingDestination={clearMappingDestination}
                                  removeMapping={removeMapping}
                                  allowCustomFields={allowCustomFields}
                                  showMoveControls={sortOrder === 'mapping'}
                                  dragHandleProps={provided.dragHandleProps}
                                  isDragging={snapshot.isDragging}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            {/* Incomplete mappings (missing source or destination) */}
            {additionalMappings.some((m) => !m.from || !m.to) && (
              <div id="incomplete-mappings">
                <div className="sync-refresh mt-4">
                  {!allowCustomFields && additionalMappings.some((m) => m.from && !m.to) ? (
                    <h3>Incomplete Mappings <small>(remove or add field(s) to the destination)</small></h3>
                  ) : (
                    <h3>Incomplete Mappings <small>(remove or select a source)</small></h3>
                  )}
                  <Button color="link" size="sm" onClick={() => setConfirmRemoveIncompleteModal(true)} className="btn-link-census">
                    <i className="bi bi-x" /> remove all
                  </Button>
                </div>
                {!allowCustomFields && additionalMappings.some((m) => m.from && !m.to) && (
                  <Alert color="warning" className="mt-3 alert-icon-left">
                    <i className="bi bi-exclamation-triangle-fill" />
                    <div>
                      Some source fields don't have matching destination fields. Create them in your destination first, then click <Button color="link" size="sm" className="alert-link p-0 align-baseline" onClick={() => handleRefreshFields()}>refresh fields</Button> to update options. Alternatively, remove these mappings.
                    </div>
                  </Alert>
                )}
                <div className="incomplete-mappings mapping-block">
                  {getSortedMappings(additionalMappings)
                    .filter(({ mapping }) => !mapping.from || !mapping.to)
                    .map(({ mapping, originalIndex }) => (
                      <SingleFieldMapper
                        key={originalIndex}
                        mapping={mapping}
                        index={originalIndex}
                        columns={columns}
                        allDestinationFields={effectiveDestinationFields}
                        usedDestinationFieldsMap={getUsedDestinationFieldsMap()}
                        getAvailableFieldsForMapping={getAvailableFieldsForMapping}
                        updateAdditionalMapping={updateAdditionalMapping}
                        clearMappingDestination={clearMappingDestination}
                        removeMapping={removeMapping}
                        incompleteLabel={<Badge color="warning" className="ms-2">INCOMPLETE</Badge>}
                        allowCustomFields={allowCustomFields}
                      />
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!isRefreshingFields && (
          <div className="bottom-button-row">
            <Button onClick={addMapping} >
              <i className="fa-solid fa-plus" />
              Add Field Mapping
            </Button>
            <div className="sync-actions">
              <Button className="primary" onClick={onCancel}>
                Cancel
              </Button>
              <Button solid className="promote" disabled={!isValid()} onClick={handleCreateSyncClick}>
                Create Sync
              </Button>
            </div>
          </div>
        )}
      </CardBody>

      <Modal centered backdrop isOpen={confirmRefreshModal} toggle={() => setConfirmRefreshModal(false)}>
        <ModalHeader toggle={() => setConfirmRefreshModal(false)}>
          <i className="bi bi-exclamation-triangle me-2" />
          Confirm Refresh
        </ModalHeader>
        <ModalBody>
          Refreshing fields will cause a loss of field mapping changes. Do you want to continue?
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={() => setConfirmRefreshModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmRefreshModal(false);
              handleRefreshFields();
            }}
          >
            Continue
          </Button>
        </ModalFooter>
      </Modal>

      <Modal centered backdrop isOpen={confirmRemoveIncompleteModal} toggle={() => setConfirmRemoveIncompleteModal(false)}>
        <ModalHeader toggle={() => setConfirmRemoveIncompleteModal(false)}>
          <i className="bi bi-exclamation-triangle me-2" />
          Confirm Remove All
        </ModalHeader>
        <ModalBody>
          This will remove all incomplete field mappings. Do you want to continue?
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={() => setConfirmRemoveIncompleteModal(false)}>
            Cancel
          </Button>
          <Button onClick={removeAllIncompleteMappings}>
            Continue
          </Button>
        </ModalFooter>
      </Modal>

      <Modal centered backdrop size="lg" isOpen={confirmSyncModal} toggle={() => setConfirmSyncModal(false)}>
        <ModalHeader toggle={() => setConfirmSyncModal(false)}>
          <i className="bi bi-check-circle me-2" />
          Confirm Sync Configuration
        </ModalHeader>
        <ModalBody>
          <div className="sync-confirm-summary">
            <div className="sync-confirm-row">
              <strong>Order:</strong>
              <span>{orderName} <Badge color="secondary">#{orderId}</Badge></span>
            </div>
            <div className="sync-confirm-row">
              <strong>Destination:</strong>
              <span>{destinationName}</span>
            </div>
            <div className="sync-confirm-row">
              <strong>Destination Object:</strong>
              <span>{destinationObject?.label || destinationObject?.full_name}</span>
            </div>
            <div className="sync-confirm-row">
              <strong>Operation:</strong>
              <Badge className="badge-primary-id">{OPERATION_LABELS[selectedOperation] || selectedOperation}</Badge>
            </div>
          </div>

          <h5 className="mt-4 mb-3">Field Mappings</h5>
          <div className="sync-confirm-table-wrapper">
          <Table bordereless size="sm" className="sync-confirm-table">
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '30px' }} />
              <col style={{ width: '35%' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Source Column</th>
                <th></th>
                <th>Destination Field</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {primaryMappingRequired && primaryMapping.from && primaryMapping.to && (
                <tr className="table-primary-identifier">
                  <td className="truncate-cell"><TruncatedText text={primaryMapping.from} className="truncated-cell-text" /></td>
                  <td><i className="bi bi-arrow-right" /></td>
                  <td className="truncate-cell"><TruncatedText text={primaryMapping.to} className="truncated-cell-text" /></td>
                  <td>
                    <Badge className="badge-primary-id me-1">Primary Identifier</Badge>
                    {primaryMapping.isAutoCreated ? (
                      <Badge className="badge-auto-created">+ Auto-created Field</Badge>
                    ) : primaryMapping.isCustom ? (
                      <Badge className="badge-custom">+ Custom Field</Badge>
                    ) : !destFieldExists(primaryMapping.to) ? (
                      <Badge className="badge-auto-created">+ Auto-created Field</Badge>
                    ) : null}
                  </td>
                </tr>
              )}
              {(sortOrder === 'alphabetical'
                ? [...additionalMappings].filter((m) => m.from && m.to).sort((a, b) => (a.to || '').toLowerCase().localeCompare((b.to || '').toLowerCase()))
                : additionalMappings.filter((m) => m.from && m.to)
              ).map((mapping, idx) => (
                  <tr key={idx}>
                    <td className="truncate-cell"><TruncatedText text={mapping.from} className="truncated-cell-text" /></td>
                    <td><i className="bi bi-arrow-right" /></td>
                    <td className="truncate-cell"><TruncatedText text={mapping.to} className="truncated-cell-text" /></td>
                    <td>
                      {mapping.isAutoCreated ? (
                        <Badge className="badge-auto-created">+ Auto-created Field</Badge>
                      ) : mapping.isCustom ? (
                        <Badge className="badge-custom">+ Custom Field</Badge>
                      ) : mapping.isAutoMapped ? (
                        <Badge className="badge-matched">= Matched Field</Badge>
                      ) : !destFieldExists(mapping.to) ? (
                        <Badge className="badge-auto-created">+ Auto-created Field</Badge>
                      ) : null}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="primary" onClick={() => setConfirmSyncModal(false)}>
            Cancel
          </Button>
          <Button className="promote" onClick={handleConfirmSync}>
            Sync Order Data
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  )
}

